import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Bell, CheckCheck, Calendar, Info, Building2, Trash2 } from 'lucide-react'
import { notificacionService } from '../../services/notificacionService'
import { useNotifications } from '../../contexts/NotificationContext'
import LoadingState from '../../components/ui/LoadingState'
import EmptyState from '../../components/ui/EmptyState'
import PushNotificationButton from '../../components/PushNotificationButton'

function extractNotificationId(item = {}) {
  return item.id_notificacion || item.idNotificacion || item.id || item.notificacion_id || item.notificacionId || null
}

function toBoolRead(item = {}) {
  if (typeof item.leida === 'boolean') return item.leida
  if (typeof item.leido === 'boolean') return item.leido
  if (typeof item.is_read === 'boolean') return item.is_read
  if (typeof item.isRead === 'boolean') return item.isRead
  if (item.fecha_lectura || item.fechaLectura || item.read_at || item.readAt) return true
  return false
}

function formatNotificationDate(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString('es-ES', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function normalizeNotification(item = {}) {
  const meta = item.metadata_json || item.metadataJson || item.metadata || {}
  return {
    ...item,
    id: extractNotificationId(item),
    tipo: item.tipo_notificacion || item.tipoNotificacion || item.tipo || '',
    titulo: item.titulo || item.title || item.asunto || 'Notificación',
    mensaje: item.mensaje || item.message || item.descripcion || item.body || 'Sin contenido',
    leida: toBoolRead(item),
    fecha: item.fecha_creacion || item.fechaCreacion || item.created_at || item.createdAt || item.fecha || '',
    ruta:
      item.ruta ||
      item.path ||
      item.link ||
      item.enlace ||
      item.route ||
      item.url ||
      item.data?.ruta ||
      item.data?.link ||
      item.data?.path ||
      meta.ruta ||
      meta.link ||
      ''
  }
}

function parseNotificationsResponse(response) {
  const raw = response?.data ?? response
  const items = Array.isArray(raw?.data)
    ? raw.data
    : Array.isArray(raw?.data?.data)
      ? raw.data.data
      : Array.isArray(raw?.notificaciones)
        ? raw.notificaciones
        : Array.isArray(raw)
          ? raw
          : []

  return items
    .map(normalizeNotification)
    .sort((a, b) => {
      const da = new Date(a.fecha || 0).getTime()
      const db = new Date(b.fecha || 0).getTime()
      const safeA = Number.isNaN(da) ? 0 : da
      const safeB = Number.isNaN(db) ? 0 : db
      return safeB - safeA
    })
}

function getTypeIcon(tipo = '') {
  const value = String(tipo).toUpperCase()
  if (value.includes('CITA')) return Calendar
  if (value.includes('AUTORIZ')) return Building2
  if (value.includes('SISTEMA')) return Info
  return Bell
}

function resolveNotificationPath(item = {}) {
  const directPath =
    item.ruta ||
    item.path ||
    item.link ||
    item.enlace ||
    item.route ||
    item.url ||
    item.data?.ruta ||
    item.data?.path ||
    item.data?.link ||
    item.data?.url

  if (typeof directPath === 'string' && directPath.trim()) {
    const value = directPath.trim()
    if (!/^https?:\/\//i.test(value)) {
      return value.startsWith('/') ? value : `/${value}`
    }
  }

  const tipo = String(item.tipo || item.tipo_notificacion || item.tipoNotificacion || '').toUpperCase()
  const citaId = item.id_cita || item.idCita || item.cita_id || item.citaId || item.data?.id_cita || item.data?.idCita
  const fichaId = item.id_ficha || item.idFicha || item.ficha_id || item.fichaId || item.data?.id_ficha || item.data?.idFicha
  const mascotaId =
    item.id_mascota ||
    item.idMascota ||
    item.mascota_id ||
    item.mascotaId ||
    item.data?.id_mascota ||
    item.data?.idMascota ||
    item.metadata_json?.id_mascota ||
    item.metadataJson?.id_mascota ||
    item.metadata?.id_mascota
  const accion = String(item.accion_requerida || item.accionRequerida || item.action || item.data?.accion || item.data?.accion_requerida || '').toUpperCase()
  const condicionIdFromAction = accion.startsWith('CONDICION:') ? accion.split(':')[1] : null

  if (tipo.includes('CITA')) return citaId ? `/citas/${citaId}` : '/citas'
  if (tipo.includes('FICHA')) return fichaId ? `/fichas/${fichaId}` : '/fichas'
  if (tipo.includes('MASCOTA')) return mascotaId ? `/mascotas/${mascotaId}` : '/mascotas'
  // Condiciones de salud: por ahora navegar al perfil de la mascota si viene id_mascota, o a mascotas como fallback.
  // (El backend envía accion_requerida tipo "CONDICION:<id_mascota_condicion>".)
  if (tipo.includes('CONDICION') || tipo.includes('SALUD') || accion.startsWith('CONDICION:')) {
    if (mascotaId) return `/mascotas/${mascotaId}`
    // Si en el futuro agregamos ruta dedicada a condiciones, se puede usar condicionIdFromAction aquí.
    if (condicionIdFromAction) return '/mascotas'
  }
  if (tipo.includes('AUTORIZ') || tipo.includes('SUSCRIP') || tipo.includes('CLINICA')) return '/suscripciones-clinicas'
  return '/notificaciones'
}

export default function NotificacionesMobileExact() {
  const navigate = useNavigate()
  const {
    contadorNoLeidas,
    marcarLeida,
    marcarTodasLeidas,
    ocultarNotificacion,
    ocultarTodasNotificaciones,
    registerRefreshListener
  } = useNotifications()

  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true)
      const response = await notificacionService.getAll({ per_page: 25, page: 1 })
      setItems(parseNotificationsResponse(response))
    } catch (error) {
      console.error('Error loading mobile notifications:', error)
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchItems()
    const unregister = registerRefreshListener(fetchItems)
    return () => unregister()
  }, [fetchItems, registerRefreshListener])

  const handleOpen = async (item) => {
    const id = item.id
    if (!item.leida && id) {
      try {
        await marcarLeida(id)
        setItems((prev) => prev.map((n) => {
          const nid = n.id
          return nid === id ? { ...n, leida: true } : n
        }))
      } catch (error) {
        console.error('Error marking notification as read:', error)
      }
    }

    const destination = resolveNotificationPath(item)
    if (destination && destination !== '/notificaciones') {
      navigate(destination)
    }
  }

  const handleMarkAll = async () => {
    try {
      await marcarTodasLeidas()
      setItems((prev) => prev.map((n) => ({ ...n, leida: true })))
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
    }
  }

  const handleDeleteAll = async () => {
    if (!window.confirm('¿Borrar todas las notificaciones?')) return
    try {
      const ok = await ocultarTodasNotificaciones()
      if (ok) {
        setItems([])
        return
      }
      // Fallback: si el backend no tiene /ocultar-todas aún, borrar en lote lo cargado.
      const ids = items.map((n) => n.id).filter(Boolean)
      if (ids.length === 0) return
      await Promise.allSettled(ids.map((id) => notificacionService.deleteSoft(id)))
      setItems([])
    } catch (error) {
      console.error('Error soft-deleting all notifications:', error)
      const msg = error?.response?.data?.error || error?.message || 'No se pudieron borrar las notificaciones.'
      window.alert(msg)
    }
  }

  const handleSoftDelete = async (event, item) => {
    event.stopPropagation()
    if (!item?.id) return
    if (!window.confirm('¿Borrar esta notificación?')) return
    try {
      const ok = await ocultarNotificacion(item.id)
      if (ok) setItems((prev) => prev.filter((n) => n.id !== item.id))
    } catch (error) {
      console.error('Error soft-deleting notification:', error)
      const msg = error?.response?.data?.error || error?.message || 'No se pudo borrar la notificación.'
      window.alert(msg)
    }
  }

  if (loading) {
    return <LoadingState message="Cargando notificaciones..." />
  }

  return (
    <div className="min-h-full bg-white pb-20">
      <div className="px-4 pt-3 pb-2 border-b border-[#f0f0f0] flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="h-8 w-8 rounded-full flex items-center justify-center text-[#555]"
          type="button"
          aria-label="Volver"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-[17px] font-bold text-[#111] flex-1">Notificaciones</h1>
        <div className="flex items-center gap-2">
          {contadorNoLeidas > 0 && (
            <button
              onClick={handleMarkAll}
              className="h-8 px-2.5 rounded-full bg-[#f4faf4] border border-[#d8ead8] text-[#4a7a4a] text-[12px] font-semibold inline-flex items-center gap-1"
              type="button"
              aria-label="Marcar todas como leídas"
              title="Marcar todas como leídas"
            >
              <CheckCheck className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Marcar</span>
            </button>
          )}
          {items.length > 0 && (
            <button
              onClick={handleDeleteAll}
              className="h-8 px-2.5 rounded-full bg-[#fff3f0] border border-[#ffd7cf] text-[#b84c33] text-[12px] font-semibold inline-flex items-center gap-1"
              type="button"
              aria-label="Borrar todas las notificaciones"
              title="Borrar todas"
            >
              <Trash2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Borrar</span>
            </button>
          )}
        </div>
      </div>

      <div className="px-4 py-3">
        <div className="rounded-2xl bg-gradient-to-r from-[#f0f6e8] to-[#e8f0fd] border border-[#e7edf6] px-3.5 py-3 animate-fade-in">
          <p className="text-[11px] text-[#6e7f91]">Centro de alertas</p>
          <p className="text-[13px] font-semibold text-[#1f2a37] mt-0.5">
            {contadorNoLeidas > 0 ? `Tienes ${contadorNoLeidas} notificaciones sin leer` : 'Estás al día con tus notificaciones'}
          </p>
        </div>
      </div>

      <div className="px-4 pb-2">
        <div className="rounded-2xl border border-[#e7edf6] bg-[#f8fafc] px-3.5 py-3">
          <p className="text-[12px] font-semibold text-[#1f2a37]">Notificaciones push</p>
          <p className="text-[11px] text-[#7b8794] mt-0.5 mb-2">
            Activa o desactiva alertas del dispositivo desde este centro.
          </p>
          <PushNotificationButton />
        </div>
      </div>

      {items.length === 0 ? (
        <div className="px-4 py-6">
          <EmptyState
            icon={Bell}
            title="No hay notificaciones"
            message="Aún no tienes alertas nuevas."
          />
        </div>
      ) : (
        <div className="px-4 space-y-2 pb-3">
          {items.map((item) => {
            const id = item.id
            const Icon = getTypeIcon(item.tipo)
            const unread = !item.leida
            return (
              <div
                key={id || item.fecha || item.titulo}
                role="button"
                tabIndex={0}
                onClick={() => handleOpen(item)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleOpen(item)
                }}
                className={`w-full text-left px-3.5 py-3 rounded-2xl border flex items-start gap-3 transition-all duration-200 hover:-translate-y-[1px] ${unread ? 'bg-[#f0f6e8] border-[#dce8c8]' : 'bg-white border-[#edf1f5]'}`}
              >
                <span className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${unread ? 'bg-[#e8f0fd] text-[#5b87d8]' : 'bg-[#f0f0f0] text-[#777]'}`}>
                  <Icon className="w-4 h-4" />
                </span>
                <span className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-[#111] truncate">{item.titulo}</p>
                  <p className="text-[11px] text-[#888] mt-0.5 line-clamp-2">{item.mensaje}</p>
                  <p className="text-[10px] text-[#bbb] mt-1">{formatNotificationDate(item.fecha)}</p>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    className="h-8 w-8 rounded-full bg-white/80 border border-[#edf1f5] flex items-center justify-center text-[#a44] hover:bg-white"
                    onClick={(e) => handleSoftDelete(e, item)}
                    aria-label="Borrar notificación"
                    title="Borrar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                  {unread && <span className="mt-0.5 h-2 w-2 rounded-full bg-[#7a9a52]" />}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
